// 005 US2, FR-020..FR-028 (P-PROD-1..6): one category for the selected child. School Books has the
// subject chips (All subjects, English, Mathematics, Science, Social Studies, Languages, Workbooks)
// and the booklist chip; Stationery lists its items without chips. Products are the server's, paged
// with Show more. Web wide: breadcrumb, grid and the cart panel in a right rail; phones: the
// "Ordering for" choices on top and the cart bar at the bottom.
import { Link } from "expo-router";
import { useEffect, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { FullSetButton, useBooklist } from "./BooklistSection";
import { CartSummary } from "./CartSummary";
import { cartMessage, SHOP_COPY } from "./copy";
import { OrderingFor } from "./OrderingFor";
import { ProductCard } from "./ProductCard";
import { type CategoryCode, type ProductQuery, useShopping } from "./shopApi";
import { useLines } from "./useLines";
import { classSectionText, useShopper } from "./useShopper";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: t.color.background },
    scroll: { flex: 1 },
    page: {
      width: "100%",
      maxWidth: 1180,
      alignSelf: "center",
      paddingHorizontal: t.space.lg,
      paddingTop: t.space.lg,
      paddingBottom: t.space.x3l,
      gap: t.space.md,
    },
    columns: {
      flexDirection: "row",
      gap: t.space.x2l,
      alignItems: "flex-start",
    },
    main: { flex: 1, gap: t.space.md },
    crumbs: { flexDirection: "row", gap: t.space.xs },
    chips: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      minHeight: t.size.button,
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      justifyContent: "center",
    },
    chipOn: { borderColor: t.color.accent, backgroundColor: t.color.accent },
    chipBooklist: { borderColor: t.color.primary },
    chipTextOn: { color: t.color.onAccent },
    grid: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    cell: { width: 170, flexGrow: 1, maxWidth: 240 },
    more: { alignSelf: "center", minWidth: 200 },
  }),
);

export const CATEGORY_TITLES: Record<CategoryCode, string> = {
  SCHOOL_BOOKS: "School Books",
  STATIONERY: "Stationery",
  UNIFORMS: "Uniforms",
};

type Chip =
  | {
      id: string;
      label: string;
      query: Omit<ProductQuery, "category" | "page">;
    }
  | { id: "booklist"; label: string };

const BOOK_CHIPS: Chip[] = [
  { id: "all", label: SHOP_COPY.allSubjects, query: {} },
  { id: "ENGLISH", label: "English", query: { subject: "ENGLISH" } },
  {
    id: "MATHEMATICS",
    label: "Mathematics",
    query: { subject: "MATHEMATICS" },
  },
  { id: "SCIENCE", label: "Science", query: { subject: "SCIENCE" } },
  {
    id: "SOCIAL_STUDIES",
    label: "Social Studies",
    query: { subject: "SOCIAL_STUDIES" },
  },
  {
    id: "LANGUAGES",
    label: SHOP_COPY.languages,
    query: { subjectGroup: "LANGUAGES" },
  },
  { id: "WORKBOOK", label: SHOP_COPY.workbooks, query: { kind: "WORKBOOK" } },
];

function useProducts(
  studentId: string | null,
  category: CategoryCode,
  chip: Chip,
) {
  const { client } = useShopping();
  const [pages, setPages] = useState<{
    key: string;
    items: Api["CatalogueProduct"][];
    page: number;
    totalPages: number;
  } | null>(null);
  const [failure, setFailure] = useState<{
    key: string;
    error: AppError;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const query = "query" in chip ? chip.query : null;
  // The chip's query as text, so the effect depends on what is asked rather than on a new object.
  const queryText = query ? JSON.stringify(query) : null;
  const key = `${studentId}|${category}|${queryText}`;
  const fetchPage = (page: number) =>
    client.listProducts(studentId!, { category, page, ...(query ?? {}) });
  useEffect(() => {
    if (!studentId || !queryText) return;
    let live = true;
    const chipQuery = JSON.parse(queryText) as Omit<
      ProductQuery,
      "category" | "page"
    >;
    client.listProducts(studentId, { category, page: 1, ...chipQuery }).then(
      (p) =>
        live &&
        setPages({
          key,
          items: [...p.items],
          page: 1,
          totalPages: p.page.totalPages,
        }),
      (e: AppError) => live && setFailure({ key, error: e }),
    );
    return () => {
      live = false;
    };
  }, [client, studentId, category, queryText, key]);
  const current = pages?.key === key ? pages : null;
  return {
    items: current?.items ?? null,
    error: failure?.key === key && !current ? failure.error : null,
    hasMore: current ? current.page < current.totalPages : false,
    loadingMore,
    async more() {
      if (!current) return;
      setLoadingMore(true);
      try {
        const p = await fetchPage(current.page + 1);
        setPages({
          key,
          items: [...current.items, ...p.items],
          page: current.page + 1,
          totalPages: p.page.totalPages,
        });
      } catch (e) {
        setFailure({ key, error: e as AppError });
      } finally {
        setLoadingMore(false);
      }
    },
  };
}

export function CategoryScreen({
  category,
  embedded = false,
}: {
  category: CategoryCode;
  /** 010: inside the booking screen, which has its own scroll, heading and banner. */
  embedded?: boolean;
}) {
  const styles = useStyles();
  const wide = useChromeKind() === "desktop";
  const shopper = useShopper();
  const studentId = shopper.studentId;
  const books = category === "SCHOOL_BOOKS";
  const booklist = useBooklist(books ? studentId : null);
  const published = booklist.booklist?.published ? booklist.booklist : null;
  const cls = shopper.student
    ? classSectionText(shopper.student.classLevel, shopper.student.section)
    : "";
  const chips: Chip[] = books
    ? [
        ...(published
          ? [
              {
                id: "booklist" as const,
                label: SHOP_COPY.booklistChip(
                  `${cls.replace(/([A-Z])$/, " · Section $1")}`,
                ),
              },
            ]
          : []),
        ...BOOK_CHIPS,
      ]
    : [{ id: "all", label: SHOP_COPY.allSubjects, query: {} }];
  const [chipId, setChipId] = useState("all");
  const chip =
    chips.find((c) => c.id === chipId) ?? chips.find((c) => c.id === "all")!;
  const products = useProducts(studentId, category, chip);
  const lines = useLines(studentId, shopper.schoolActive);
  const title = CATEGORY_TITLES[category];

  const grid = (
    items: { card: Parameters<typeof ProductCard>[0]["product"] }[],
  ) => (
    <View style={styles.grid}>
      {items.map(({ card }) => (
        <View key={card.id} style={styles.cell}>
          <ProductCard product={card} {...lines.line(card.id)} />
        </View>
      ))}
    </View>
  );

  const body =
    chip.id === "booklist" && published ? (
      <>
        {grid(
          published.items.map((i) => ({
            card: {
              id: i.productId,
              name: i.name,
              kind: i.kind,
              publisher: i.publisher,
              priceInPaise: i.unitPriceInPaise,
              imageUrl: i.imageUrl,
              orderable: true,
              available: i.available,
              booklist: { mandatory: i.mandatory, quantity: i.quantity },
            },
          })),
        )}
        {shopper.student ? (
          <FullSetButton
            booklist={published}
            studentId={shopper.student.id}
            studentName={shopper.student.name}
            disabled={!shopper.schoolActive}
            short
          />
        ) : null}
      </>
    ) : products.error ? (
      <InlineMessage
        tone="error"
        detail={
          products.error.requestId
            ? `Reference: ${products.error.requestId}`
            : null
        }
      >
        {products.error.code === "STUDENT_NOT_LINKED"
          ? cartMessage(products.error).text
          : SHOP_COPY.loadFailed}
      </InlineMessage>
    ) : products.items === null ? (
      <Text tone="muted">…</Text>
    ) : products.items.length === 0 ? (
      <Text tone="muted">{SHOP_COPY.noProducts}</Text>
    ) : (
      <>
        {grid(
          products.items.map((p) => {
            const onList = published?.items.find((i) => i.productId === p.id);
            return {
              card: {
                id: p.id,
                name: p.name,
                kind: p.kind,
                classLevels: p.classLevels,
                publisher: p.publisher,
                priceInPaise: p.priceInPaise,
                imageUrl: p.imageUrl,
                orderable: p.orderable,
                ...(onList
                  ? {
                      booklist: {
                        mandatory: onList.mandatory,
                        quantity: onList.quantity,
                      },
                    }
                  : {}),
              },
            };
          }),
        )}
        {products.hasMore ? (
          <View style={styles.more}>
            <Button
              variant="outline"
              pending={products.loadingMore}
              onPress={() => void products.more()}
            >
              {SHOP_COPY.loadMore}
            </Button>
          </View>
        ) : null}
      </>
    );

  const main = (
    <View style={styles.main}>
      {wide && !embedded ? (
        <View style={styles.crumbs} role="navigation" aria-label="Breadcrumb">
          <Link href="/">
            <Text tone="muted">Home</Text>
          </Link>
          <Text tone="muted">/</Text>
          <Text>{title}</Text>
        </View>
      ) : null}
      {embedded ? null : (
        <Text variant="display" accessibilityRole="header" aria-level={1}>
          {title}
        </Text>
      )}
      {books ? <Text tone="muted">{SHOP_COPY.booksNote}</Text> : null}
      {!wide && !embedded ? <OrderingFor /> : null}
      {!shopper.schoolActive && shopper.student?.school ? (
        <InlineMessage tone="warning">
          {SHOP_COPY.schoolNotActive(shopper.student.school.name)}
        </InlineMessage>
      ) : null}
      {!lines.online ? (
        <InlineMessage tone="info">{SHOP_COPY.offline}</InlineMessage>
      ) : null}
      {books ? (
        <View style={styles.chips} role="radiogroup" aria-label="Subjects">
          {chips.map((c) => {
            const on = c.id === chip.id;
            return (
              <PressableTarget
                key={c.id}
                accessibilityRole="radio"
                accessibilityLabel={c.label}
                aria-checked={on}
                accessibilityState={{ checked: on }}
                onPress={() => setChipId(c.id)}
                style={[
                  styles.chip,
                  c.id === "booklist" && styles.chipBooklist,
                  on && styles.chipOn,
                ]}
              >
                <Text
                  weight="semiBold"
                  style={on ? styles.chipTextOn : undefined}
                >
                  {c.label}
                </Text>
              </PressableTarget>
            );
          })}
        </View>
      ) : null}
      {body}
      {lines.error && lines.error.key !== "load" ? (
        <InlineMessage
          tone="error"
          detail={
            lines.error.error.requestId
              ? `Reference: ${lines.error.error.requestId}`
              : null
          }
        >
          {cartMessage(lines.error.error).text}
        </InlineMessage>
      ) : null}
    </View>
  );

  if (embedded) {
    return (
      <View style={styles.main} testID={`category-${category}`}>
        {main}
        <CartSummary variant="panel" selectedStudentId={studentId} />
      </View>
    );
  }

  return (
    <View style={styles.root} testID={`category-${category}`}>
      <ScrollView style={styles.scroll}>
        <View style={styles.page} role="main">
          {wide ? (
            <View style={styles.columns}>
              {main}
              <View>
                <OrderingFor />
                <CartSummary variant="panel" selectedStudentId={studentId} />
              </View>
            </View>
          ) : (
            main
          )}
        </View>
      </ScrollView>
      {!wide ? (
        <CartSummary variant="bar" selectedStudentId={studentId} />
      ) : null}
    </View>
  );
}
